import { t } from 'i18next';
import React, { useMemo } from 'react';
import styled from 'styled-components';

import BarChart from '../../components/Charts/BarChart/BarChart';
import ComponentCircleLoader from '../../components/Loaders/ComponentCircleLoader';
import { DashboardCardTitle } from '../../styledComponents/dashboard';

const TaskChartContainer = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  margin-top: 20px;
  border: 1px solid
    ${({ cardType, theme }) =>
      cardType ? theme.dashboardColors[cardType] : theme.colors.main.textMain};
`;

const ChartTitle = styled.h3`
  font-size: 18px;
  color: ${({ theme }) => theme.font?.pageText?.color || '#3c4043'};
  margin-bottom: 20px;
`;

/**
 * Component to display task progress by office using a vertical bar chart
 * @param {Object} props - Component props
 * @param {Array} props.data - Task progress data by office
 * @param {boolean} props.isLoading - Loading state
 */
const TaskProgressBarChart = ({ data = [], isLoading = false }) => {
  // Process data for the bar chart
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data.map(office => {
      // Calculate total tasks for this office - handle null/undefined values gracefully
      const total =
        (office.completed || 0) +
        (office.cancelled || 0) +
        (office.assigned || 0) +
        (office.missed || 0) +
        (office.closed || 0) +
        (office.re_assigned || 0) +
        // (office.approved || 0) +
        // (office.rejected || 0) +
        // (office.pending || 0) +
        (office.in_progress || 0);

      return {
        id: office.office,
        label: office.office,
        completed: office.completed || 0,
        cancelled: office.cancelled || 0,
        assigned: office.assigned || 0,
        missed: office.missed || 0,
        closed: office.closed || 0,
        re_assigned: office.re_assigned || 0,
        // approved: office.approved || 0,
        // rejected: office.rejected || 0,
        // pending: office.pending || 0,
        in_progress: office.in_progress || 0,
        total: total,
      };
    });
  }, [data]);

  // Calculate the maximum value for the chart (max of all office totals)
  const maxTotal = useMemo(() => {
    if (!chartData || chartData.length === 0) return 0;
    return Math.max(...chartData.map(item => item.total));
  }, [chartData]);

  // Define series for the chart (different task statuses)
  const chartSeries = [
    { key: 'completed', name: t('completed'), color: '#4CAF50' },
    { key: 'cancelled', name: t('cancelled'), color: '#FF5252' },
    { key: 'assigned', name: t('assigned'), color: '#2196F3' },
    { key: 'missed', name: t('missed'), color: '#9C27B0' },
    { key: 'closed', name: t('closed'), color: '#607D8B' },
    { key: 're_assigned', name: t('re_assigned'), color: '#FF9800' },
    { key: 'approved', name: t('approved'), color: '#8BC34A' },
    { key: 'rejected', name: t('rejected'), color: '#F44336' },
    { key: 'pending', name: t('pending'), color: '#FFC107' },
    { key: 'in_progress', name: t('in_progress'), color: '#03A9F4' },
  ];

  if (isLoading) {
    return (
      <TaskChartContainer>
        <ComponentCircleLoader padding="5vh" />
      </TaskChartContainer>
    );
  }

  return (
    <TaskChartContainer>
      <DashboardCardTitle>
        {t('ticket_progress_overview_by_offices')}
      </DashboardCardTitle>{' '}
      <BarChart
        data={chartData}
        series={chartSeries}
        xAxisLabel={t('offices')}
        height={450}
        showLegend={true}
        maxValue={maxTotal}
      />
    </TaskChartContainer>
  );
};

export default TaskProgressBarChart;
